<template>
  <aside class="detail-panel">
    <h2 class="panel-title">详情 / 编辑</h2>
    <button v-if="item" class="add-event-button" :disabled="locked" @click="$emit('add-event')">↗ 为{{ item.itemType === 'node' ? '此节点' : '此链路' }}添加演化事件</button>

    <template v-if="form">
      <div class="detail-head">
        <span class="type-tag">{{ form.itemType === 'edge' ? '链路' : '节点' }}</span>
        <span class="status-badge" :style="{ background: statusColor + '22', color: statusColor }">
          <i class="dot" :style="{ background: statusColor }"></i>{{ statusLabel }}
        </span>
      </div>

      <div class="field">
        <label>名称</label>
        <input :disabled="locked" v-model="form.label" type="text" placeholder="显示名" />
      </div>

      <div v-if="form.itemType === 'node'" class="field">
        <label>设备分类</label>
        <select :disabled="locked" v-model="form.category" aria-label="设备分类">
          <option v-for="c in categories" :key="c" :value="c">{{ categoryLabels[c] || c }}</option>
        </select>
      </div>

      <div v-if="form.itemType === 'node'" class="field model-field">
        <label>设备品牌</label>
        <select :disabled="locked" v-model="brandFilter" aria-label="设备品牌"><option value="">全部品牌</option><option v-for="brand in modelBrands" :key="brand" :value="brand">{{ brand }}</option></select>
        <label>设备型号</label>
        <select :disabled="locked" v-model="form.modelId" aria-label="设备型号">
          <option value="default">默认图形（通用节点）</option>
          <option v-for="model in filteredModelOptions" :key="model.id" :value="model.id">
            {{ model.name }}
          </option>
        </select>
        <div v-if="selectedModel" class="model-summary">
          <span class="model-format">{{ selectedModel.source === 'uploaded' ? '上传模型' : '内置模型' }}</span>
          <strong>{{ selectedModel.name }}</strong>
          <small v-if="selectedModel.brand">{{ selectedModel.brand }} · {{ selectedModel.productModel }}</small>
          <small>{{ selectedModel.fidelity || '上传模型 · 参数由上传者提供' }}</small>
          <a v-if="selectedModel.referenceUrl" :href="selectedModel.referenceUrl" target="_blank" rel="noopener noreferrer">查看官方规格</a>
          <span>最长边 {{ selectedModel.length }} 个场景单位</span>
          <div class="model-specs" aria-label="型号参数">
            <div
              v-for="parameter in selectedModel.parameters"
              :key="parameter.label"
              class="model-spec-row"
            >
              <span>{{ parameter.label }}</span>
              <strong>{{ parameter.value }}</strong>
            </div>
          </div>
          <small>应用后立即替换当前设备外观</small>
        </div>
      </div>

      <template v-else>
        <div class="field">
          <label>信道编号</label>
          <input :disabled="locked" v-model="form.linkId" type="text" placeholder="如 C0006（可空）" />
        </div>
        <div class="field-row">
          <div class="field">
            <label>链路类型</label>
            <select :disabled="locked" v-model="form.linkType">
              <option value="wired">有线</option>
              <option value="wireless">无线</option>
              <option value="">未指定</option>
            </select>
          </div>
          <div class="field">
            <label>容量（Mbps）</label>
            <input :disabled="locked" v-model="form.value" type="number" placeholder="如 300" />
          </div>
        </div>
      </template>

      <div class="field">
        <label>状态</label>
        <select :disabled="locked" v-model="form.status">
          <option v-for="s in statuses" :key="s.key" :value="s.key">
            {{ s.label }}
          </option>
        </select>
      </div>

      <div class="meta-editor">
        <div class="meta-editor-head">
          <span>参数 / 指标</span>
          <button :disabled="locked" class="mini-btn" @click="addMetaRow">+ 添加</button>
        </div>
        <div v-for="(row, i) in form.metaRows" :key="i" class="meta-row">
          <input :disabled="locked" v-model="row.k" class="meta-k" type="text" placeholder="键" />
          <input :disabled="locked" v-model="row.v" class="meta-v" type="text" placeholder="值" />
          <button :disabled="locked" class="mini-btn danger" @click="removeMetaRow(i)">×</button>
        </div>
        <div v-if="!form.metaRows.length" class="meta-empty">暂无参数，点击“添加”</div>
      </div>

      <template v-if="form.itemType === 'edge'">
        <div
          v-for="side in nicSides"
          :key="side.key"
          class="meta-editor nic-editor"
        >
          <div class="meta-editor-head">
            <span>{{ side.label }}（接口）</span>
            <button :disabled="locked" class="mini-btn" @click="addNicRow(side.key)">+ 指标</button>
          </div>
          <div class="field">
            <input :disabled="locked"
              v-model="form[side.key + 'NicId']"
              type="text"
              placeholder="接口 id，如 N0011:IF000002（可空）"
            />
          </div>
          <div
            v-for="(row, i) in form[side.key + 'NicRows']"
            :key="i"
            class="meta-row"
          >
            <input :disabled="locked" v-model="row.k" class="meta-k" type="text" placeholder="键" />
            <input :disabled="locked" v-model="row.v" class="meta-v" type="text" placeholder="值" />
            <button :disabled="locked" class="mini-btn danger" @click="removeNicRow(side.key, i)">
              ×
            </button>
          </div>
          <div v-if="!form[side.key + 'NicRows'].length" class="meta-empty">
            暂无接口指标（如 发送速率、发送数据包、队列容量）
          </div>
        </div>
      </template>

      <div class="actions">
        <button :disabled="locked" class="apply-btn" @click="apply">应用修改</button>
        <button :disabled="locked" class="delete-btn" @click="remove">删除</button>
      </div>
      <p class="edit-note">修改会写回拓扑图，并在“演化”时一并发送给后端。</p>
    </template>

    <div v-else class="empty-hint">
      点击画布中的节点或链路进行编辑；新增节点、连边后会自动选中以便填写参数。
    </div>

    <div class="legend">
      <div class="legend-title">状态图例</div>
      <div v-for="s in statuses" :key="s.key" class="legend-row">
        <i class="dot" :style="{ background: s.color }"></i>{{ s.label }}
      </div>
      <div class="legend-note">· 节点光晕 / 链路颜色 = 状态 · 边线粗细 = 容量 / 吞吐</div>
      <div class="legend-note">· 实线 = 有线链路 · 虚线 = 无线链路</div>
      <div class="legend-note">· 节点按分类使用不同三维模型（路由器 / 交换机 / 基站 / 卫星 / 无人机 / 终端 …）</div>
    </div>
  </aside>
</template>

<script>
import { STATUS_STYLES, NODE_CATEGORIES, STATUS_VALUES, CATEGORY_LABELS } from '../api/topology'
import { getNodeModelOptions, getNodeModelSpec, resolveNodeModelCategory } from '../graph/modelLibrary'

// 把值尽量还原为数字（保持 STN 规范化数值字段为数字类型）。
function coerce(value) {
  const text = String(value).trim()
  if (text === '') return ''
  if (!Number.isNaN(Number(text)) && /^-?\d/.test(text)) return Number(text)
  return text
}

export default {
  name: 'TopologyDetail',
  props: {
    item: { type: Object, default: null },
    locked: Boolean
  },
  emits: ['update', 'delete', 'add-event'],
  data() {
    return {
      form: null,
      brandFilter: '',
      categories: NODE_CATEGORIES,
      categoryLabels: CATEGORY_LABELS,
      statusValues: STATUS_VALUES,
      nicSides: [
        { key: 'source', label: '源网卡' },
        { key: 'target', label: '目标网卡' }
      ]
    }
  },
  computed: {
    statusColor() {
      return (STATUS_STYLES[this.form?.status] || STATUS_STYLES.normal).color
    },
    statusLabel() {
      return (STATUS_STYLES[this.form?.status] || STATUS_STYLES.normal).label
    },
    statuses() {
      return Object.entries(STATUS_STYLES).map(([key, v]) => ({ key, ...v }))
    },
    modelBrands() { return [...new Set(this.modelOptions.map(model => model.brand || '未填写品牌'))] },
    filteredModelOptions() { return this.modelOptions.filter(model => !this.brandFilter || (model.brand || '未填写品牌') === this.brandFilter) },
    modelOptions() {
      return getNodeModelOptions(this.form?.category)
    },
    selectedModel() {
      return this.modelOptions.find(({ id }) => id === this.form?.modelId) || null
    }
  },
  watch: {
    brandFilter(brand) {
      if (!brand || !this.form || this.form.itemType !== 'node') return
      const matches = this.modelOptions.filter(model => (model.brand || '未填写品牌') === brand)
      if (!matches.some(model => model.id === this.form.modelId)) this.form.modelId = matches[0]?.id || 'default'
    },
    item: {
      immediate: true,
      handler(next) {
        this.brandFilter = ''
        this.form = next ? this.buildForm(next) : null
      }
    },
    'form.category'(category) {
      this.brandFilter = ''
      if (!this.form || this.form.itemType !== 'node') return
      const options = getNodeModelOptions(category)
      if (this.form.modelId !== 'default' && !options.some(({ id }) => id === this.form.modelId)) {
        this.form.modelId = 'default'
      }
    }
  },
  methods: {
    buildForm(item) {
      // 网卡对象形如 { id, meta?, props?, status? }；把 meta/props 合并成可编辑行。
      const nic = (n) => {
        const merged = { ...((n && n.props) || {}), ...((n && n.meta) || {}) }
        return {
          id: (n && n.id) || '',
          status: (n && n.status) || '',
          rows: Object.entries(merged).map(([k, v]) => ({ k, v: String(v) }))
        }
      }
      const s = nic(item.sourceNic)
      const t = nic(item.targetNic)
      const modelSpec = resolveNodeModelCategory(item) === 'generic' ? null : getNodeModelSpec(item.category, item.modelId, item.id)
      return {
        itemType: item.itemType,
        g6Id: item.g6Id,
        label: item.label || '',
        category: item.category || 'router',
        modelId: modelSpec?.id || 'default',
        status: item.status || 'normal',
        linkId: item.linkId || '',
        linkType: item.linkType || '',
        value: typeof item.value === 'number' ? item.value : '',
        metaRows: Object.entries(item.meta || {}).map(([k, v]) => ({
          k,
          v: String(v)
        })),
        sourceNicId: s.id,
        sourceNicStatus: s.status,
        sourceNicRows: s.rows,
        targetNicId: t.id,
        targetNicStatus: t.status,
        targetNicRows: t.rows
      }
    },
    addMetaRow() {
      this.form.metaRows.push({ k: '', v: '' })
    },
    removeMetaRow(index) {
      this.form.metaRows.splice(index, 1)
    },
    addNicRow(side) {
      this.form[`${side}NicRows`].push({ k: '', v: '' })
    },
    removeNicRow(side, index) {
      this.form[`${side}NicRows`].splice(index, 1)
    },
    // 从表单字段组装一个后端可用的 NIC 对象；空则返回 null（表示清空该端）。
    buildNic(side) {
      const id = String(this.form[`${side}NicId`]).trim()
      const meta = {}
      for (const { k, v } of this.form[`${side}NicRows`]) {
        const key = String(k).trim()
        if (key) meta[key] = coerce(v)
      }
      if (!id && !Object.keys(meta).length) return null
      const nic = { meta }
      if (id) nic.id = id
      // 保留演化写入的网卡状态（本面板不编辑它）。
      const status = this.form[`${side}NicStatus`]
      if (status) nic.status = status
      return nic
    },
    buildMeta() {
      const meta = {}
      for (const { k, v } of this.form.metaRows) {
        const key = String(k).trim()
        if (!key) continue
        meta[key] = coerce(v)
      }
      return meta
    },
    apply() {
      const f = this.form
      const model = {
        label: String(f.label).trim(),
        status: f.status,
        meta: this.buildMeta()
      }
      if (f.itemType === 'node') {
        model.category = f.category
        model.modelId = f.modelId
      } else {
        model.linkId = String(f.linkId).trim()
        model.linkType = f.linkType
        model.value = f.value === '' ? undefined : Number(f.value)
        // 两端网卡一并写回（null 表示清空该端）。
        model.sourceNic = this.buildNic('source')
        model.targetNic = this.buildNic('target')
      }
      this.$emit('update', {
        itemType: f.itemType,
        g6Id: f.g6Id,
        model
      })
    },
    remove() {
      this.$emit('delete', this.form.g6Id)
    }
  }
}
</script>

<style scoped>
.add-event-button{width:100%;margin-bottom:14px;padding:10px;border:1px solid #d9c9f4;border-radius:7px;background:#f2ecfb;color:#7950cd;cursor:pointer}
.detail-panel {
  width: 300px;
  box-sizing: border-box;
  padding: 20px;
  background: #fff;
  border-left: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.panel-title {
  margin: 0 0 16px;
  font-size: 18px;
  color: #1f1f1f;
}
.detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.type-tag {
  font-size: 12px;
  color: #1890ff;
  background: #e6f7ff;
  border-radius: 3px;
  padding: 1px 6px;
}
.status-badge {
  margin-left: auto;
  font-size: 12px;
  border-radius: 10px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  flex: 1;
}
.field-row {
  display: flex;
  gap: 10px;
}
.field > label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}
.field input,
.field select {
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}
.field input:focus,
.field select:focus {
  outline: none;
  border-color: #1890ff;
}
.model-field {
  margin-top: 2px;
}
.model-summary {
  position: relative;
  display: grid;
  gap: 3px;
  margin-top: 7px;
  padding: 9px 10px;
  color: #475569;
  background: linear-gradient(135deg, #f0f7ff, #f8fafc);
  border: 1px solid #cfe3f8;
  border-radius: 6px;
  font-size: 11px;
}
.model-summary > strong {
  color: #16324f;
  font-size: 12px;
  padding-right: 66px;
}
.model-summary small {
  color: #7890a8;
}
.model-specs {
  display: grid;
  gap: 4px;
  margin: 5px 0 3px;
  padding-top: 7px;
  border-top: 1px solid #dbe8f5;
}
.model-spec-row {
  display: grid;
  grid-template-columns: 68px minmax(0, 1fr);
  align-items: baseline;
  gap: 8px;
}
.model-spec-row span {
  color: #7890a8;
}
.model-spec-row strong {
  min-width: 0;
  color: #29445e;
  font-size: 11px;
  font-weight: 600;
  text-align: right;
  overflow-wrap: anywhere;
}
.model-format {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 2px 5px;
  color: #1677ff;
  background: #e6f4ff;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.meta-editor {
  margin: 6px 0 12px;
  border-top: 1px dashed #eaeaea;
  padding-top: 10px;
}
.nic-editor {
  border-top: none;
  border-left: 2px solid #91d5ff;
  padding: 6px 0 6px 10px;
  margin: 0 0 10px;
}
.nic-editor .field {
  margin-bottom: 8px;
}
.meta-editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #595959;
  margin-bottom: 8px;
}
.meta-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}
.meta-k {
  width: 42%;
}
.meta-v {
  flex: 1;
  min-width: 0;
}
.meta-row input {
  padding: 5px 7px;
  font-size: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-sizing: border-box;
}
.mini-btn {
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  padding: 2px 8px;
  cursor: pointer;
}
.mini-btn.danger {
  color: #cf1322;
  border-color: #ffccc7;
}
.meta-empty {
  color: #bfbfbf;
  font-size: 12px;
  padding: 4px 0;
}
.actions {
  display: flex;
  gap: 8px;
}
.apply-btn {
  flex: 1;
  padding: 8px;
  background: #1890ff;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}
.apply-btn:hover {
  background: #40a9ff;
}
.delete-btn {
  padding: 8px 14px;
  background: #fff;
  color: #cf1322;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}
.edit-note {
  font-size: 12px;
  color: #8c8c8c;
  margin: 8px 0 0;
}
.empty-hint {
  color: #bfbfbf;
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}
.legend {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px dashed #eaeaea;
  font-size: 12px;
  color: #595959;
}
.legend-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.legend-row {
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 22px;
}
.legend-note {
  margin-top: 8px;
  color: #8c8c8c;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>

